import React, { memo } from 'react';
import moment from 'moment';
import { Formik } from 'formik';
import { has, isEmpty, sortBy } from 'lodash';
import * as Yup from 'yup';

import Dialog from 'components/ui/Dialog';
import { getAppointmentsByClinicId } from 'api/appointment';
import { FormSectionTitle } from 'styles/form';
import Select from 'components/ui/Select';
import Form from 'components/ui/Form';
import { exportAppointmentsPdf } from './export-pdf-helper';

const ExportPdfModal = ({
  exportModalOpen,
  setExportModalOpen,
  slots,
  clinic,
  siteName,
}) => {
  const handleModalClose = () => {
    setExportModalOpen(false);
  };

  const handleExportPdf = async (values) => {
    const { date } = values;
    const { id, schedule } = clinic;
    const { custom_field: customField } = schedule || {};

    const { data: appointmentsData } = await getAppointmentsByClinicId(
      date,
      id,
    );

    const formattedAppointments = appointmentsData.data.map(
      ({ relationShips, start_date: startDate }) => {
        const { participant } = relationShips;
        const {
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          custom_field_value: customFieldValue,
        } = participant[0];

        return {
          timeSlot: startDate,
          firstName,
          lastName,
          customFieldValue,
          email,
          phone,
        };
      },
    );

    let tableData = [];

    const selectedDateAppointments = clinic.times.filter(
      ({ start_date: slotStartDate }) =>
        moment(slotStartDate, 'YYYY-MM-DD HH:mm:ss').format('MM-DD-YYYY') ===
        date,
    );

    selectedDateAppointments.forEach((timeSlot) => {
      const { start_date: startDate, count } = timeSlot;
      const matchedAppointments = sortBy(
        formattedAppointments.filter(
          (appointment) => appointment.timeSlot === startDate,
        ),
        ['firstName'],
      );

      if (!isEmpty(matchedAppointments)) {
        tableData = [...tableData, ...matchedAppointments];
      }

      if (count > 0) {
        const emptySlots = Array(count).fill({
          timeSlot: startDate,
        });

        tableData = [...tableData, ...emptySlots];
      }
    });

    const exportData = tableData.map((data) => ({
      ...data,
      timeSlot: moment(data.timeSlot, 'YYYY-MM-DD HH:mm:ss').format('h:mma'),
    }));

    exportAppointmentsPdf(date, clinic, exportData, siteName, customField);
  };

  return (
    <Formik
      initialValues={{
        date: new Date(),
      }}
      validationSchema={Yup.object().shape({
        date: Yup.date().required('field is required'),
      })}
      enableReinitialize
      onSubmit={handleExportPdf}
    >
      {({ values: { date }, errors, handleChange, submitForm, touched }) => (
        <Dialog
          open={exportModalOpen}
          handleClose={handleModalClose}
          disableTopPadding
          maxWidth="xs"
          typeText="Appointment"
          submitForm={submitForm}
          disableDelete
          buttonText="Save as PDF"
        >
          <FormSectionTitle>Export Appointments PDF</FormSectionTitle>
          <Form fullWidth onSubmit={submitForm}>
            {slots && (
              <Select
                options={Object.keys(slots).map((slot) => {
                  if (isEmpty(slots)) {
                    return;
                  }

                  return {
                    label: moment(slot).format('ddd - MMM DD, YYYY'),
                    value: slot,
                  };
                })}
                label="Date"
                variant="outlined"
                errors={touched.date && has(errors, 'date')}
                touched={touched}
                helperText={touched.date && errors.date}
                name="date"
                onChange={handleChange}
                value={date}
                disableNone
                required
              />
            )}
          </Form>
        </Dialog>
      )}
    </Formik>
  );
};

export default memo(ExportPdfModal);
